import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Heart, ArrowRight, Sparkles, Edit3 } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

// Emotion tags as OPTIONAL quick reactions (secondary to free text)
const EMOTION_TAGS = [
  { id: 'aligned', emoji: '✨', label_fr: 'Alignée', label_en: 'Aligned', label_pt: 'Alinhada' },
  { id: 'inspired', emoji: '🔥', label_fr: 'Inspirée', label_en: 'Inspired', label_pt: 'Inspirada' },
  { id: 'motivated', emoji: '💎', label_fr: 'Motivée', label_en: 'Motivated', label_pt: 'Motivada' },
  { id: 'confused', emoji: '🤔', label_fr: 'Confuse', label_en: 'Confused', label_pt: 'Confusa' }
];

const EmotionCheckpoint = ({ 
  step, 
  onComplete
}) => {
  const [feedbackText, setFeedbackText] = useState('');
  const [selectedEmotion, setSelectedEmotion] = useState(null);
  const [consentTestimonial, setConsentTestimonial] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [existingCheckpoint, setExistingCheckpoint] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  
  const { token } = useAuth();
  const { i18n } = useTranslation();
  const lang = i18n.language || 'fr';

  // Load existing checkpoint data if available (for updating)
  useEffect(() => {
    const loadExistingCheckpoint = async () => {
      if (!token || !step) return;
      try {
        const response = await axios.get(
          `${API}/emotion-checkpoint/step/${step}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.has_feedback && response.data.checkpoint) {
          const cp = response.data.checkpoint;
          setExistingCheckpoint(cp);
          setFeedbackText(cp.feedback_text || '');
          setSelectedEmotion(cp.emotion_tag || null);
          setConsentTestimonial(cp.consent_testimonial || false);
          setIsUpdating(true);
        }
      } catch (err) {
        console.error('Error loading existing checkpoint:', err);
      }
    };
    loadExistingCheckpoint();
  }, [token, step]);

  const getLabel = (tag) => {
    const key = `label_${lang}`;
    return tag[key] || tag.label_en;
  };

  // Validation: user must write something OR select an emotion
  const canSubmit = feedbackText.trim().length > 0 || selectedEmotion;

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error('Prends un moment pour exprimer ton ressenti');
      return;
    }

    setIsSubmitting(true);
    try {
      if (isUpdating && existingCheckpoint) {
        // Update existing checkpoint
        await axios.put(
          `${API}/emotion-checkpoint/step/${step}`,
          {
            feedback_text: feedbackText.trim() || null,
            emotion_tag: selectedEmotion,
            consent_testimonial: consentTestimonial
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Create new checkpoint
        await axios.post(
          `${API}/emotion-checkpoint`,
          {
            step,
            feedback_text: feedbackText.trim() || null,
            emotion_tag: selectedEmotion,
            consent_testimonial: consentTestimonial
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      setSubmitted(true);
      setTimeout(() => {
        onComplete?.();
      }, 2500);
    } catch (err) {
      console.error('Emotion checkpoint error:', err);
      toast.error('Une erreur est survenue');
      setIsSubmitting(false);
    }
  };

  // Success state after submission
  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-[#030303] flex items-center justify-center px-6"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center max-w-md"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="w-20 h-20 mx-auto mb-8 bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/40 rounded-full flex items-center justify-center"
          >
            <Heart className="text-[#D4AF37]" size={32} fill="#D4AF37" />
          </motion.div>
          
          <motion.p
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-white text-2xl md:text-3xl mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Merci pour ce moment
          </motion.p>
          
          <motion.p
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-[#A1A1AA] text-base leading-relaxed"
          >
            Chaque réflexion te rapproche de ta vision.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="mt-8 flex items-center justify-center gap-2 text-[#D4AF37]/50 text-sm"
          >
            <Sparkles size={14} />
            <span>Tu avances.</span>
          </motion.div>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-[#030303] flex flex-col items-center justify-center px-6 py-12"
    >
      <div className="w-full max-w-lg">
        {/* Header */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-10"
        >
          <p className="text-[#D4AF37]/60 text-xs tracking-[0.25em] uppercase mb-6">
            {isUpdating ? 'Mettre à jour ton ressenti' : 'Prends un moment'}
          </p>
          
          <h2
            className="text-white text-2xl md:text-3xl lg:text-4xl leading-relaxed"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Comment te sens-tu après cette étape ?
          </h2>
        </motion.div>

        {/* Main expression area */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="space-y-8"
        >
          {/* Central text field - the heart of the experience */}
          <div className="relative">
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Exprime ce que tu ressens, ce que cela t'a apporté, où tu en es maintenant..."
              className="w-full h-48 bg-[#0A0A0A]/80 border border-[#27272A] focus:border-[#D4AF37]/50 text-white px-6 py-5 text-base resize-none outline-none transition-all duration-300 leading-relaxed placeholder:text-[#52525B] placeholder:leading-relaxed"
              style={{ fontFamily: "'Inter', sans-serif" }}
              data-testid="checkpoint-feedback-textarea"
            />
            
            {/* Subtle edit indicator for update mode */}
            {isUpdating && (
              <div className="absolute top-4 right-4 flex items-center gap-1.5 text-[#D4AF37]/40">
                <Edit3 size={12} />
                <span className="text-xs">Modifier</span>
              </div>
            )}
          </div>

          {/* Optional emotion tags - secondary, not primary */}
          <div className="pt-2">
            <p className="text-[#52525B] text-xs mb-4 text-center">
              Ou ajoute une réaction rapide (optionnel)
            </p>
            <div className="flex justify-center gap-2 flex-wrap">
              {EMOTION_TAGS.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => setSelectedEmotion(selectedEmotion === tag.id ? null : tag.id)}
                  className={`px-4 py-2.5 border transition-all duration-300 ${
                    selectedEmotion === tag.id
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-white'
                      : 'border-[#1C1C1C] text-[#71717A] hover:border-[#27272A] hover:text-[#A1A1AA]'
                  }`}
                  data-testid={`emotion-tag-${tag.id}`}
                >
                  <span className="mr-2">{tag.emoji}</span>
                  <span className="text-sm">{getLabel(tag)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Testimonial consent */}
          <div className="flex items-start gap-3 pt-4 border-t border-[#1C1C1C]">
            <Checkbox
              id="consent"
              checked={consentTestimonial}
              onCheckedChange={setConsentTestimonial}
              className="mt-0.5 border-[#27272A] data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
            />
            <label 
              htmlFor="consent" 
              className="text-[#52525B] text-xs leading-relaxed cursor-pointer hover:text-[#71717A] transition-colors"
            >
              J'accepte que mon témoignage soit partagé pour inspirer d'autres femmes entrepreneures
            </label>
          </div>

          {/* Submit button */}
          <div className="pt-6">
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !canSubmit}
              className={`w-full py-6 text-base transition-all duration-300 ${
                canSubmit 
                  ? 'bg-[#D4AF37] hover:bg-[#B8860B] text-black' 
                  : 'bg-[#1C1C1C] text-[#52525B] cursor-not-allowed'
              }`}
              data-testid="submit-checkpoint"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <Sparkles size={18} />
                  </motion.div>
                  Envoi...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  {isUpdating ? 'Mettre à jour mon ressenti' : 'Partager mon ressenti'}
                  <ArrowRight size={18} />
                </span>
              )}
            </Button>
            
            {/* Gentle guidance */}
            {!canSubmit && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center text-[#52525B] text-xs mt-4"
              >
                Écris quelques mots ou choisis une réaction pour continuer
              </motion.p>
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default EmotionCheckpoint;
